import { gsap } from 'gsap';
import { Graphics, Sprite, type Texture } from 'pixi.js';
import type { Actor } from '../../battle/types';
import { DESIGN_HEIGHT } from '../../data/constants';
import type { ArenaPlayContext } from '../types';

// Y of the embedded ball in design-px - lodged in the upper wall area, well
// above character bodies and torch heads. Tune if arena art changes.
const EMBED_Y = 150;
// Horizontal offset from the defender's baseX, in design-px. Sign is relative
// to the defender's facing - positive = away from arena center (toward the
// outer wall on the defender's side), negative = toward center. 0 = directly
// above the defender's centerline.
const EMBED_X_OFFSET = 120;
// Visual size for the CLEAN cannonball sprite (in flight phase, falling).
// MUST match Cannonball.TARGET_SIZE in src/skills/SparkyBot/Cannon/Cannonball.ts -
// on block the flying Cannonball is disposed at impact and a fresh embedded
// sprite spawns at the same spot. Mismatched sizes cause a visible "pop" at
// the swap. If you tweak one, tweak the other.
const EMBED_SIZE = 25;
// Visual size for WALL-ART sprites (cannonball-in-wall, damaged-wall). These
// textures contain the ball PLUS surrounding wall damage (cracks, debris), so
// fitting them to EMBED_SIZE would make the ball portion appear tiny. Tune
// independently from EMBED_SIZE so the ball portion of the in-wall sprite
// looks the same visual size as the clean ball.
const LODGED_SIZE = 90;
// Random tilt range so the ball reads as "stuck at an angle" instead of
// perfectly upright.
const EMBED_MAX_TILT_RAD = 0.4;
// Fly-in duration when spawn is given a fromPosition (e.g. cannonball impact).
// Keep short - this is the tail of the cannon's ricochet, not a fresh trip.
const FLYIN_DURATION = 0.035;
const FLYIN_SPIN_RAD = Math.PI;

// Drop trajectory: a single accelerating fall (gravity-like, never decelerates)
// with a brief sideways drift at the start - ball nudges out of the wall hole
// and then just falls. Y uses one continuous power2.in over the whole drop so
// there's no velocity reset mid-fall. X drifts in parallel during the first
// portion only. Durations are in BATTLE-pace seconds (gsap globalTimeline
// timeScale slows them).
const DROP_TOTAL_DURATION = 0.55;
// Fraction of the total fall during which the sideways drift happens. After
// this, X is locked and only Y continues accelerating downward.
const DROP_DRIFT_FRAC = 0.35;
// Horizontal drift in design-px. Sign is flipped per side via defender.facing
// so the ball drifts INWARD (toward arena center, away from the wall it came
// out of) regardless of which side it was lodged on - reads as the ball
// scattering into the open arena rather than further into the wall.
const DROP_SIDEWAYS_DRIFT = 5;
// Nudge applied to the clean falling ball's start position (in design-px).
// The ball inherits ball.x/ball.y from the lodged sprite which is anchored at
// the texture center, but the artist may have drawn the ball off-center within
// the wall-art texture. Tune so the clean ball starts at the same spot the
// painted ball appeared inside the in-wall sprite. X is mirrored per side via
// defender.facing - positive = INWARD (toward arena center), negative = OUTWARD.
const DROP_START_OFFSET_X = 20;
const DROP_START_OFFSET_Y = 10;
const DROP_ROTATION_RAD = Math.PI * 1.5;
// Floor Y for the dropped ball. Matches BattleScene's character baseline so
// the ball lands on the same ground plane characters stand on.
const GROUND_Y = DESIGN_HEIGHT - 50;

// Placeholder explosion. Single bright disc that expands and fades.
const EXPLOSION_DURATION = 0.35;
const EXPLOSION_BASE_RADIUS = 14;
const EXPLOSION_MAX_SCALE = 3.4;
const EXPLOSION_COLOR = 0xffaa44;

// One ball per side. Single-battle assumption matches the rest of the engine.
// Module-scoped so attach in one event and release in a later event find each
// other without threading state through ArenaPlayContext.
const embedded: Partial<Record<Actor, Sprite>> = {};

// Clears the per-battle registry. Called from the arena's mount() so a fresh
// battle never inherits a stale (already destroyed) ball from a previous one -
// battles are restartable from the desktop screen now.
export function resetEmbeddedBalls(): void {
  embedded.p1 = undefined;
  embedded.p2 = undefined;
}

// Spawns an embedded cannonball over the given side. Idempotent: if a ball is
// already lodged on that side, returns immediately (the engine-emitted
// arena_attach event may fire after a skill has already placed the ball - we
// want the skill's path to win).
//
// If `fromPosition` is given, the ball starts there with the flight texture
// and animates into the wall pose (used by cannon to make the ricochet read
// as the ball flying into the wall). Without it, the ball appears directly
// at its final position.
//
// If `lodgedTexture` is given, the sprite swaps to it the moment the ball
// reaches the wall (after fly-in, or immediately on direct spawn) - lets the
// "stuck in wall" art (cracks, partial embed) replace the in-flight cannonball.
export async function spawnEmbeddedBall(
  side: Actor,
  texture: Texture,
  ctx: ArenaPlayContext,
  opts?: {
    fromPosition?: { x: number; y: number };
    lodgedTexture?: Texture;
  },
): Promise<void> {
  if (embedded[side]) return;

  // Initial texture: flight sprite if there's a fly-in to play, else jump
  // straight to the lodged pose (no in-flight phase to render). Picks the
  // matching size since flight and lodged sprites have different scales.
  const startsLodged = !opts?.fromPosition && !!opts?.lodgedTexture;
  const initialTexture = startsLodged ? opts.lodgedTexture! : texture;
  const ball = new Sprite(initialTexture);
  ball.anchor.set(0.5);
  fitTexture(ball, initialTexture, startsLodged ? LODGED_SIZE : EMBED_SIZE);
  // Lodged-art textures are drawn for the LEFT wall - mirror on the right.
  // Clean cannonball is symmetric so the mirror is a visual no-op there;
  // applying unconditionally on lodged-only branch keeps the logic obvious.
  if (startsLodged) applyWallMirror(ball, side);

  const defender = ctx.scene.getSprite(side);
  // facing is +1 for p1 (faces right -> outer wall is to the LEFT, i.e.
  // negative-X), -1 for p2 (faces left -> outer wall is to the RIGHT). So the
  // "outward" direction is -facing.
  const finalX = defender.baseX + EMBED_X_OFFSET * -defender.facing;
  const finalY = EMBED_Y;
  const finalRotation = (Math.random() - 0.5) * 2 * EMBED_MAX_TILT_RAD;

  if (opts?.fromPosition) {
    ball.position.set(opts.fromPosition.x, opts.fromPosition.y);
    ball.rotation = 0;
  } else {
    ball.position.set(finalX, finalY);
    ball.rotation = finalRotation;
  }

  ctx.scene.spawnEffect(ball);
  embedded[side] = ball;

  if (opts?.fromPosition) {
    await new Promise<void>((resolve) => {
      gsap.to(ball, {
        x: finalX,
        y: finalY,
        rotation: finalRotation + FLYIN_SPIN_RAD,
        duration: FLYIN_DURATION,
        // Linear - ball flies into the wall at constant speed and stops dead
        // on contact (no decel easing that would read as "slowing down before
        // sticking").
        ease: 'none',
        onComplete: resolve,
      });
    });
    // After fly-in: swap to the "stuck in wall" sprite (if provided) and drop
    // the spin contribution so the lodged art settles at its natural tilt.
    // Lodged sprite uses LODGED_SIZE - it's a wall-art texture (ball + cracks).
    if (opts.lodgedTexture) {
      ball.texture = opts.lodgedTexture;
      fitTexture(ball, opts.lodgedTexture, LODGED_SIZE);
      applyWallMirror(ball, side);
      ball.rotation = finalRotation;
    }
  }
}

function fitTexture(sprite: Sprite, texture: Texture, targetSize: number): void {
  const longest = Math.max(texture.width, texture.height);
  if (longest > 0) sprite.scale.set(targetSize / longest);
}

// Wall-art textures (cannonball-in-wall, damaged-wall) are drawn for the LEFT
// wall orientation. Mirror horizontally on the right side so asymmetric
// details (crack directions, etc.) look natural for both. MUST be called AFTER
// fitTexture - flips the sign of existing scale.x.
function applyWallMirror(sprite: Sprite, side: Actor): void {
  if (side === 'p2') sprite.scale.x = -sprite.scale.x;
}

// Drops the embedded ball in an L-shaped path: first sideways out of the wall
// hole toward the defender's vertical axis, then straight down to the feet.
// Plays a placeholder explosion at landing. Idempotent - if no ball is embedded
// on this side, returns immediately.
export async function dropAndExplodeBall(
  side: Actor,
  ctx: ArenaPlayContext,
): Promise<void> {
  const ball = embedded[side];
  if (!ball) return;
  embedded[side] = undefined;

  // Snapshot wall pose before mutating the ball - the damage mark sits at the
  // exact embed point (LODGED_SIZE matches the lodged ball it replaces; same
  // tilt for visual continuity) and stays there for the rest of the battle.
  const damagedWallTex = ctx.textures.damagedWall;
  if (damagedWallTex) {
    const mark = new Sprite(damagedWallTex);
    mark.anchor.set(0.5);
    fitTexture(mark, damagedWallTex, LODGED_SIZE);
    applyWallMirror(mark, side);
    mark.position.set(ball.x, ball.y);
    mark.rotation = ball.rotation;
    ctx.scene.spawnEffect(mark);
    // Re-add ball so it renders ABOVE the new mark while falling (spawnEffect
    // adds to the top of particleLayer; calling it on an existing child moves
    // it back to the top).
    ctx.scene.spawnEffect(ball);
  }

  // What falls is the clean cannonball (EMBED_SIZE), not the "stuck in wall"
  // art - the lodged-version stays imprinted as the damage mark above.
  // Rotation kept for visual continuity. Position gets a tunable nudge so the
  // clean (small) ball starts where the painted ball actually was inside the
  // wall-art sprite, not at the texture's geometric center.
  const cleanTex = ctx.textures.cannonball;
  if (cleanTex) {
    ball.texture = cleanTex;
    fitTexture(ball, cleanTex, EMBED_SIZE);
  }

  // Drop is anchored to the ball's current (wall) position, not the defender.
  // facing picks the INWARD direction so the ball drifts toward arena center
  // (away from the wall it came out of) - reads as scattering into open space.
  const inwardSign = ctx.scene.getSprite(side).facing;
  ball.x += DROP_START_OFFSET_X * inwardSign;
  ball.y += DROP_START_OFFSET_Y;
  const startX = ball.x;
  const landingX = startX + DROP_SIDEWAYS_DRIFT * inwardSign;
  const landingY = GROUND_Y;
  const driftDuration = DROP_TOTAL_DURATION * DROP_DRIFT_FRAC;

  await new Promise<void>((resolve) => {
    const tl = gsap.timeline({ onComplete: resolve });
    // Y: one continuous accelerating fall (gravity). Never decelerates - the
    // ball only picks up speed from start to landing.
    tl.to(
      ball,
      { y: landingY, duration: DROP_TOTAL_DURATION, ease: 'power2.in' },
      0,
    );
    // X: short sideways drift during the first portion of the fall. Linear so
    // it doesn't decelerate either. Runs in parallel with Y.
    tl.to(
      ball,
      { x: landingX, duration: driftDuration, ease: 'none' },
      0,
    );
    // Spin in parallel for the whole drop - matches the Y accel curve.
    tl.to(
      ball,
      { rotation: ball.rotation + DROP_ROTATION_RAD, duration: DROP_TOTAL_DURATION, ease: 'power2.in' },
      0,
    );
  });

  ball.parent?.removeChild(ball);
  ball.destroy();

  await spawnPlaceholderExplosion(landingX, landingY, ctx);
}

function spawnPlaceholderExplosion(
  x: number,
  y: number,
  ctx: ArenaPlayContext,
): Promise<void> {
  // Simple expand-fade disc. Additive blend so it warms the scene like a flash.
  const disc = new Graphics().circle(0, 0, EXPLOSION_BASE_RADIUS).fill(EXPLOSION_COLOR);
  disc.position.set(x, y);
  disc.alpha = 1;
  disc.blendMode = 'add';
  ctx.scene.spawnEffect(disc);

  return new Promise((resolve) => {
    const tl = gsap.timeline({
      onComplete: () => {
        disc.parent?.removeChild(disc);
        disc.destroy();
        resolve();
      },
    });
    tl.to(disc.scale, { x: EXPLOSION_MAX_SCALE, y: EXPLOSION_MAX_SCALE, duration: EXPLOSION_DURATION, ease: 'power2.out' }, 0);
    tl.to(disc, { alpha: 0, duration: EXPLOSION_DURATION, ease: 'power2.in' }, 0);
  });
}
